// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Capacitor } from "@capacitor/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Link, Stack } from "@mui/material";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import {
  BottomSheet,
  DialogFooter,
  Text,
  TextArea,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import { useUploadVoiceNoteToS3 } from "../api/useUploadVoiceNoteToS3";
import { useJobRequestConfiguration } from "../useJobRequestConfiguration";
import { PlacementAvailabilitySelector } from "./PlacementAvailabilitySelector";
import { VoiceNoteRecorder } from "./VoiceNoteRecorder";

interface PlacementDetailCreateApplicationDialogProps {
  modalState: UseModalState;
  workplaceName: string;
  placementCandidateId: string;
  allowRequestInterview: boolean;
  onApply: (
    workerInterviewRequest: {
      dates: Date[];
      notes: string;
    },
    workerVoiceNote?: {
      rootFolder: string;
      fileKey: string;
    }
  ) => void;
}

export function PlacementDetailCreateApplicationDialog(
  props: PlacementDetailCreateApplicationDialogProps
) {
  const { modalState, workplaceName, onApply, placementCandidateId, allowRequestInterview } = props;

  const { maxInterviewsDatesToSelect: selectableNumberOfDays, maxInterviewsDatesToBeShown } =
    useJobRequestConfiguration(allowRequestInterview);

  // Store the recorded voice note as a Blob. The actual upload to S3 happens when the form is submitted.
  const formSchema = z.object({
    availability: z
      .array(z.date({ required_error: "Select at least one date" }))
      .min(1, { message: "Select at least one date" })
      .max(selectableNumberOfDays, {
        message: `You can select up to ${selectableNumberOfDays} dates`,
      }),
    workerNote: z
      .string({
        required_error: "Please enter details for your availability",
        invalid_type_error: "Please enter details for your availability",
      })
      .trim()
      .min(1, { message: "Please enter details for your availability" })
      .max(300, { message: "Maximum length is 300 characters" }),
    workerVoiceNoteBlob: z.instanceof(Blob).optional(),
  });

  type FormData = z.infer<typeof formSchema>;

  const formMethods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      availability: [],
      workerNote: undefined,
      workerVoiceNoteBlob: undefined,
    },
  });

  const {
    formState: { isSubmitting },
  } = formMethods;

  const {
    mutateAsync: uploadVoiceNote,
    isLoading: isUploadingVoiceNote,
    error: uploadError,
  } = useUploadVoiceNoteToS3();

  const { handleSubmit, control } = formMethods;

  const onSubmit = async (data: FormData) => {
    let workerVoiceNote: { rootFolder: string; fileKey: string } | undefined;

    if (data.workerVoiceNoteBlob) {
      try {
        const { rootFolder, fileKey } = await uploadVoiceNote({
          placementCandidateId,
          fileBlob: data.workerVoiceNoteBlob,
          type: data.workerVoiceNoteBlob.type,
        });
        workerVoiceNote = { rootFolder, fileKey };
      } catch {
        // Error is handled by hook; we simply stop submission.
        return;
      }
    }

    onApply(
      {
        dates: data.availability,
        notes: data.workerNote,
      },
      workerVoiceNote
    );
  };

  const handleClose = () => {
    modalState.closeModal();
  };

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={handleClose} />}
      footer={
        <DialogFooter orientation="vertical" LinkComponent={Link}>
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isSubmitting || isUploadingVoiceNote}
            onClick={handleSubmit(onSubmit)}
          >
            Request to interview
          </LoadingButton>
        </DialogFooter>
      }
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ px: 5, py: 6 }}>
            <Stack spacing={1}>
              <Text variant="h5">Request an interview at {workplaceName}</Text>
              <Text variant="body2">
                Let {workplaceName} know when you&apos;re available for an in-person interview
              </Text>
            </Stack>

            {/* Availability selector */}
            <PlacementAvailabilitySelector
              selectableNumberOfDays={selectableNumberOfDays}
              maxVisibleDates={maxInterviewsDatesToBeShown}
            />

            <Controller
              control={control}
              name="workerNote"
              render={({ field, fieldState }) => {
                return (
                  <Stack spacing={4}>
                    <Text variant="body1">Available hours</Text>
                    <TextArea
                      {...field}
                      aria-labelledby="workerNote"
                      rows={4}
                      placeholder="For example: I'm available Monday from 9AM - 12PM and Thursday from 1PM - 6PM"
                    />
                    {fieldState.error?.message && (
                      <Text variant="body2" color={(theme) => theme.palette.error.main}>
                        {fieldState.error.message}
                      </Text>
                    )}
                  </Stack>
                );
              }}
            />

            {/* Voice note recorder – visible only on web */}
            {!Capacitor.isNativePlatform() && (
              <Controller
                control={control}
                name="workerVoiceNoteBlob"
                render={({ field }) => <VoiceNoteRecorder {...field} onChange={field.onChange} />}
              />
            )}
            {uploadError && (
              <Text variant="body2" color={(theme) => theme.palette.error.main}>
                {`Failed to upload voice note. ${uploadError.message}`}
              </Text>
            )}
          </Box>
        </form>
      </FormProvider>
    </BottomSheet>
  );
}
